import React from "react"
import { SystemContext } from "../SystemProvider"
import { HeadlinesStoreContext, LiveValuesStoreContext, NodeAlertsStoreContext, AttributeValuesStoreContext, EditModeContext } from "../SystemProvider"
import { useSyncExternalStore } from "react"
import { System } from "@hydrologiq/gql-experience/graphql"
import { SystemBasic } from "../../types/SystemTypes"
import { NodeAlertSummary, SystemViewData } from "../../types/SystemView"

const noop = () => {}

export function useSystem(): System | undefined {
  const context = React.useContext(SystemContext)
  return context?.system
}

export function useSystemInfo(): SystemBasic | undefined {
  const context = React.useContext(SystemContext)
  return context?.systemInfo
}

export function useSystemViewData(): SystemViewData | undefined {
  const context = React.useContext(SystemContext)
  return context?.systemViewData
}

export function useSystemHeadlines() {
  const store = React.useContext(HeadlinesStoreContext)
  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      if (!store) return () => {}
      return store.subscribe(onStoreChange)
    },
    [store],
  )
  const getSnapshot = React.useCallback(() => {
    return store?.get()
  }, [store])
  return useSyncExternalStore(subscribe, getSnapshot)
}

export function useSystemLoading(): boolean {
  const context = React.useContext(SystemContext)
  return context?.loading ?? false
}

export function useSystemError(): string | undefined {
  const context = React.useContext(SystemContext)
  return context?.error
}

export function useSystemRefetch(): (() => void) | undefined {
  const context = React.useContext(SystemContext)
  return context?.refetch
}

export function useUpdateSystem(): (system: Partial<System>) => void {
  const context = React.useContext(SystemContext)
  return context?.updateSystem ?? noop
}

export function useUpdateSystemInfo(): (info: SystemBasic) => void {
  const context = React.useContext(SystemContext)
  return context?.updateSystemInfo ?? noop
}

export function useIsSystemGrouped(): boolean {
  const context = React.useContext(SystemContext)
  return context?.systemInfo?.grouped || false
}

export function useIsSystemInlineMonitoring(): boolean {
  const context = React.useContext(SystemContext)
  return context?.systemInfo?.inlineMonitoring || false
}

export function useOpenAlertsDialog(): (
  assetId: string,
  alerts: NodeAlertSummary,
  title?: string,
  description?: string,
) => void {
  const context = React.useContext(SystemContext)
  return context?.openAlertsDialog ?? noop
}

/**
 * Subscribe to a single live IoT value for a specific (asset, field) pair.
 * Returns undefined when integration_management is NOT enabled (no liveValues
 * in the store), or the resolved value (number | null) when it is.
 *
 * This uses useSyncExternalStore so only this leaf component re-renders when
 * the value changes — never the parent node or the ReactFlow canvas.
 */
export function useLiveValue(assetId: string | undefined, field: string | undefined): number | null | undefined {
  const store = React.useContext(LiveValuesStoreContext)
  const key = assetId && field ? `${assetId}:${field}` : undefined

  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      if (!store) return () => {}
      return store.subscribe(onStoreChange)
    },
    [store],
  )

  const getSnapshot = React.useCallback(() => {
    if (!store || !key) return undefined
    const values = store.get()
    // If the store is empty (no liveValues at all), integration_management is not
    // enabled — return undefined so the attribute component falls back to Influx.
    if (Object.keys(values).length === 0) return undefined
    // Key exists → return the value (number | null). Key missing → null.
    return key in values ? values[key] : null
  }, [store, key])

  return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * Subscribe to a resolved attribute value for a specific (asset, attributeKey) pair.
 * Returns undefined when no attribute values are in the store, or the resolved
 * value (string | null) when present. Updated every 45s by the background poll.
 */
export function useLiveAttributeValue(assetId: string | undefined, attributeKey: string | undefined): string | null | undefined {
  const store = React.useContext(AttributeValuesStoreContext)
  const key = assetId && attributeKey ? `${assetId}:attr:${attributeKey}` : undefined

  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      if (!store) return () => {}
      return store.subscribe(onStoreChange)
    },
    [store],
  )

  const getSnapshot = React.useCallback(() => {
    if (!store || !key) return undefined
    const values = store.get()
    if (Object.keys(values).length === 0) return undefined
    return key in values ? values[key] : null
  }, [store, key])

  return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * Subscribe to node alerts for a specific asset. Re-renders only this leaf
 * component (the alert badge) when alerts change — never the parent node.
 */
export function useNodeAlerts(assetId: string | undefined): NodeAlertSummary | undefined {
  const store = React.useContext(NodeAlertsStoreContext)

  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      if (!store) return () => {}
      return store.subscribe(onStoreChange)
    },
    [store],
  )

  const getSnapshot = React.useCallback(() => {
    if (!store || !assetId) return undefined
    return store.get()[assetId]
  }, [store, assetId])

  return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * Access the edit mode state. Returns isEditMode flag and setter.
 * In read-only mode (isEditMode=false): nodes are not draggable, connections
 * cannot be made/deleted, add/remove asset actions are hidden.
 */
export function useEditMode(): { isEditMode: boolean; setEditMode: (value: boolean) => void } {
  const context = React.useContext(EditModeContext)
  return context
}
